import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

type RawFee = {
  is_active: boolean;
  amount: number;
  currency: string;
  processor_id: string;
};

type RawConsultationCode = {
  id: number;
  user: string;
  is_active: boolean;
  is_default: boolean;
  label: string;
  description: string;
  processor_id: string;
  processor_payee_id: string;
  fees: RawFee[];
};

export type ConsultationCode = {
  id: number;
  isDefault: boolean;
  label: string;
  description: string;
  activeFeeAmount: number;
  activeCurrency: string;
};

export type AddConsultationCodeRequest = {
  amount: number;
  userId: string;
  description: string;
};

export type EditConsultationCodeRequest = {
  consultationCodeId: number;
  amount?: number;
  description?: string;
  isDefault?: boolean;
  userId?: string;
};

export type DeleteConsultationCodeRequest = {
  consultationCodeId: number;
  userId?: string;
};

const formatCode = (rawCode: RawConsultationCode): ConsultationCode => {
  const activeFee = rawCode.fees.find((fee) => fee.is_active);
  if (!activeFee) {
    throw new Error('No active fee found for the consultation code');
  }

  return {
    id: rawCode.id,
    isDefault: rawCode.is_default,
    label: rawCode.label,
    description: rawCode.description,
    activeFeeAmount: activeFee.amount,
    activeCurrency: activeFee.currency,
  };
};

const formatCodes = (rawCodes: RawConsultationCode[]): ConsultationCode[] => {
  const defaultCode = rawCodes.find((code) => code.is_default);
  const filteredCodes = rawCodes.filter((code) => code.is_active);
  // Ensure that the default code is included first, if it exists
  const reorderedCodes = [
    ...(defaultCode ? [defaultCode] : []),
    ...filteredCodes.filter((code) => !code.is_default),
  ];

  return reorderedCodes.map(formatCode);
};

export const consultationCodesApi = createApi({
  reducerPath: 'consultationCodesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers) => {
      headers.set('Accept', 'application/json');
      return headers;
    },
    credentials: 'same-origin',
  }),
  tagTypes: ['ConsultationCode'],
  endpoints: (builder) => ({
    getConsultationCodesForUser: builder.query<ConsultationCode[], string | undefined>({
      query: (userId) => {
        if (!userId) {
          throw new Error('userId is required');
        }
        return `consultation-codes?user_id=${userId}`;
      },
      transformResponse: formatCodes,
      providesTags: (_result, _error, userId) => [
        { type: 'ConsultationCode', id: `user-${userId}` },
      ],
    }),

    addConsultationCode: builder.mutation<ConsultationCode, AddConsultationCodeRequest>({
      query: ({ amount, userId, description }) => {
        const amountInCents = amount * 100;

        return {
          url: 'consultation-codes',
          method: 'POST',
          body: {
            label: description,
            description,
            is_default: true,
            is_active: true,
            amount: amountInCents,
            currency: 'EUR', // TODO: Make this dynamic if needed
            user: userId,
          },
        };
      },
      transformResponse: formatCode,
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'ConsultationCode', id: `user-${userId}` },
      ],
    }),

    editConsultationCode: builder.mutation<ConsultationCode, EditConsultationCodeRequest>({
      query: ({ consultationCodeId, amount, description, isDefault }) => {
        if (!consultationCodeId) {
          throw new Error('Consultation code ID is required for editing.');
        }
        if (!amount && !description && isDefault === undefined) {
          throw new Error('At least one field (amount, description, isDefault) must be provided for editing.');
        }

        const body: {
          amount?: number;
          currency?: string;
          label?: string;
          description?: string;
          is_default?: boolean;
        } = {};

        if (amount) {
          body.amount = amount * 100;
          body.currency = 'EUR';
        }
        if (description) {
          body.label = description;
          body.description = description;
        }
        if (isDefault !== undefined) {
          body.is_default = isDefault;
        }

        return {
          url: `consultation-codes/${consultationCodeId}`,
          method: 'PUT',
          body,
        };
      },
      transformResponse: formatCode,
      invalidatesTags: (_result, _error, { userId }) => {
        return [
          { type: 'ConsultationCode', id: `user-${userId}` },
        ];
      },
    }),

    deleteConsultationCode: builder.mutation<RawConsultationCode, DeleteConsultationCodeRequest>({
      query: ({ consultationCodeId, userId }) => ({
        url: `consultation-codes/${consultationCodeId}`,
        method: 'PUT',
        body: {
          is_active: false,
        },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'ConsultationCode', id: `user-${userId}` },
      ],
    }),
  }),
});

export const {
  useAddConsultationCodeMutation,
  useEditConsultationCodeMutation,
  useDeleteConsultationCodeMutation,
  useGetConsultationCodesForUserQuery,
} = consultationCodesApi;
